// src/components/Sidebar.js
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BookingsIcon from '@mui/icons-material/EventAvailable';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

const drawerWidth = 260;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : 72,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : 72,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(to bottom, ${alpha('#4CAF50', 0.05)}, ${alpha('#4CAF50', 0.01)})`,
  },
}));

const SidebarLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2, 1),
  marginBottom: theme.spacing(1),
}));

const MenuItemButton = styled(ListItem)(({ theme, active }) => ({
  borderRadius: '8px',
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1),
  transition: 'all 0.2s',
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
  '&:hover': {
    backgroundColor: active 
      ? alpha(theme.palette.primary.main, 0.20)
      : alpha(theme.palette.action.hover, 0.1),
  },
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    minWidth: 36,
  },
  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const Sidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      active: isActive('/dashboard')
    },
    {
      text: 'Prenotazioni',
      icon: <BookingsIcon />,
      path: '/bookings',
      active: isActive('/bookings')
    },
    {
      text: 'Aree sosta',
      icon: <FmdGoodIcon />,
      path: '/stopzones',
      active: isActive('/stopzones') || location.pathname.includes('/stopzones/')
    }
  ];

  return (
    <StyledDrawer
      variant="permanent"
      open={open}
    >
      {!open && (
        <IconButton 
          onClick={toggleDrawer} 
          size="small" 
          sx={{ 
            my: 1, 
            mx: 'auto', 
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      )}
      <SidebarLogo>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: open ? 'flex-start' : 'center',
            alignItems: 'center',
            width: '100%'
          }}>
            <Avatar
              src="/logo192.png"
              alt="Logo"
              sx={{ 
                width: 42, 
                height: 42, 
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginRight: open ? 2 : 0
              }}
            />
            {open && (
              <Box>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                  Area Sosta Camper
                </Typography>
                <Typography variant="body2" noWrap color="text.secondary">
                  Corinaldo
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={toggleDrawer} size="small" sx={{ ml: 'auto', display: open ? 'block' : 'none' }}>
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
      </SidebarLogo>
      
      <Divider sx={{ mx: 2, opacity: 0.6 }} />
      
      <List sx={{ pt: 1 }}>
        {menuItems.map((item) => (
          <Tooltip 
            key={item.text} 
            title={!open ? item.text : ''} 
            placement="right"
            arrow
          >
            <MenuItemButton
              button
              component={RouterLink}
              to={item.path}
              active={item.active ? 1 : 0}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} />}
            </MenuItemButton>
          </Tooltip>
        ))}
        
        
      </List>
      
      <Box sx={{ flexGrow: 1 }} />
      
      <Box sx={{ p: 2, mt: 2 }}>
        <Button
          variant="contained"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          fullWidth
          sx={{ 
            justifyContent: open ? 'flex-start' : 'center',
            minWidth: open ? 'auto' : '40px',
            borderRadius: '8px',
            py: 1
          }}
        >
          {open ? 'Logout' : ''}
        </Button>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;